import { ReactNode, useState } from 'react';
import {
  ChevronDownIcon,
  PlusIcon,
  TrashIcon,
} from '@heroicons/react/20/solid';
import { Badge } from '@tailwindComponents/Badge';
import { Ean13Barcode } from '@tailwindComponents/Ean13Barcode';
import { PackagingHierarchy } from '@tailwindComponents/PackagingHierarchy';
import { PanelFieldBox, notSet } from '@tailwindComponents/PanelFieldBox';
import { InventorySKUListing } from '@views/StockBySKU/components/types';
import { skuPackLabel } from '../../../utils/sku';
import { SkuActionsMenu, SkuMenuOption } from './SkuActionsMenu';
import {
  DimensionsEditor,
  PanelControlRow,
  PanelImagePicker,
  PanelInput,
  PanelSelect,
  PanelTextarea,
  formatDimensions,
} from './panelControls';
import {
  AdditionalFieldDraft,
  DIMENSION_UNIT_OPTIONS,
  SKU_TYPE_OPTIONS,
  SkuPanelDraft,
  VOLUME_UNIT_OPTIONS,
  WEIGHT_UNIT_OPTIONS,
  unitLabel,
} from './panelEdit';

const additionalFieldCell =
  'flex min-h-[38px] items-center rounded-lg border border-gray-200 bg-white px-3.5 py-2 text-sm text-gray-900';

interface AdditionalFieldRowsProps {
  fields: AdditionalFieldDraft[];
  isEditing: boolean;
  isAddFieldOffered: boolean;
  onFieldChange: (index: number, change: Partial<AdditionalFieldDraft>) => void;
  onRemoveField: (index: number) => void;
  onAddField: () => void;
}

function AdditionalFieldRows({
  fields,
  isEditing,
  isAddFieldOffered,
  onFieldChange,
  onRemoveField,
  onAddField,
}: AdditionalFieldRowsProps) {
  return (
    <div className='mt-3'>
      {fields.length > 0 && (
        <div className='mb-1.5 grid grid-cols-[1fr_1fr_34px] gap-3 text-xs font-medium text-gray-700'>
          <span>Field name</span>
          <span>Value</span>
          <span />
        </div>
      )}
      {fields.map((field, index) => (
        <div
          // eslint-disable-next-line react/no-array-index-key
          key={index}
          className='mb-2 grid grid-cols-[1fr_1fr_34px] items-center gap-3'
        >
          {isEditing ? (
            <>
              <PanelInput
                ariaLabel={`Additional field ${index + 1} name`}
                placeholder='e.g. Commodity code (import)'
                value={field.name}
                onChange={(name) => onFieldChange(index, { name })}
              />
              <PanelInput
                ariaLabel={`Additional field ${index + 1} value`}
                placeholder='e.g. 94051990'
                value={field.value}
                onChange={(value) => onFieldChange(index, { value })}
              />
              <button
                type='button'
                aria-label={`Remove additional field ${index + 1}`}
                className='flex h-9 w-9 items-center justify-center text-gray-500 hover:text-danger'
                onClick={() => onRemoveField(index)}
              >
                <TrashIcon className='h-4 w-4' />
              </button>
            </>
          ) : (
            <>
              <div className={additionalFieldCell}>{field.name}</div>
              <div className={additionalFieldCell}>{field.value}</div>
              <span />
            </>
          )}
        </div>
      ))}
      {isAddFieldOffered && (
        <button
          type='button'
          className='text-sm font-medium text-primary hover:underline'
          onClick={onAddField}
        >
          {fields.length > 0
            ? '+ Add another field'
            : '+ Add additional fields (commodity codes, finish...)'}
        </button>
      )}
    </div>
  );
}

export interface SkuDetailPanelProps {
  position: number;
  sku: InventorySKUListing;
  draft: SkuPanelDraft;
  onDraftChange: (draft: SkuPanelDraft) => void;
  isEditing: boolean;
  isExpanded: boolean;
  onToggleExpand: () => void;
  imageSrc: string | null;
  isArchived: boolean;
  menuId: string;
  menuOptions: SkuMenuOption[];
  canManageSkus: boolean;
  onEdit: () => void;
  onAddPallet: () => void;
  onAddAdditionalField: () => void;
  renderPalletStep: (palletIndex: number) => ReactNode;
  draftPallet?: ReactNode;
}

export function SkuDetailPanel({
  position,
  sku,
  draft,
  onDraftChange,
  isEditing,
  isExpanded,
  onToggleExpand,
  imageSrc,
  isArchived,
  menuId,
  menuOptions,
  canManageSkus,
  onEdit,
  onAddPallet,
  onAddAdditionalField,
  renderPalletStep,
  draftPallet,
}: SkuDetailPanelProps) {
  const [isFurtherInformationOpen, setIsFurtherInformationOpen] =
    useState(false);
  const patch = (change: Partial<SkuPanelDraft>) =>
    onDraftChange({ ...draft, ...change });
  const dimensionsText = formatDimensions(draft.dimensions);
  const skuAsEdited: InventorySKUListing = {
    ...sku,
    name: draft.name,
    unitOfMeasure: draft.unitOfMeasure,
    amountOfProductInSku: Number(draft.amountOfProductInSku),
  };
  const packLabel = skuPackLabel(skuAsEdited, skuAsEdited.name);

  const stageChosenImage = (chosenImageFile: File) => {
    if (!isEditing) onEdit();
    patch({ chosenImageFile });
  };

  const updateAdditionalField = (
    index: number,
    change: Partial<AdditionalFieldDraft>
  ) =>
    patch({
      additionalFields: draft.additionalFields.map((field, fieldIndex) =>
        fieldIndex === index ? { ...field, ...change } : field
      ),
    });

  return (
    <div
      data-connector-panel
      className='relative rounded-md border border-gray-300 bg-gray-50/50'
    >
      <button
        type='button'
        aria-expanded={isExpanded}
        className='flex w-full items-center gap-2 px-4 py-3 text-left text-sm font-semibold text-gray-900'
        onClick={onToggleExpand}
      >
        <span>
          SKU {position}
          {packLabel && ` · ${packLabel}`}
        </span>
        <span className='truncate text-xs font-normal text-gray-500'>
          {[draft.name, draft.skuId].filter(Boolean).join(' · ')}
        </span>
        <Badge
          intent={isArchived ? 'neutral' : 'info'}
          label={isArchived ? 'Archived' : 'Active'}
        />
        <ChevronDownIcon
          className={`ml-auto h-4 w-4 text-gray-500 transition-transform ${
            isExpanded ? 'rotate-180' : ''
          }`}
        />
      </button>

      {isExpanded && (
        <div className='px-4 pb-4'>
          <div className='absolute right-3 top-3'>
            <SkuActionsMenu id={menuId} options={menuOptions} />
          </div>

          <div className='flex flex-wrap items-stretch gap-3'>
            <PanelImagePicker
              imageSrc={imageSrc}
              chosenImageFile={draft.chosenImageFile}
              imageNoun='SKU'
              altText={draft.name || draft.skuId}
              isChangeable={isEditing || canManageSkus}
              onImageChosen={stageChosenImage}
            />

            <div className='flex min-w-[170px] flex-1 flex-col gap-2.5'>
              <PanelFieldBox
                isEmphasised
                label='SKU ID'
                isEditing={isEditing}
                value={draft.skuId || notSet}
                editor={
                  <PanelInput
                    ariaLabel='SKU ID'
                    value={draft.skuId}
                    onChange={(skuId) => patch({ skuId })}
                  />
                }
              />
              <PanelFieldBox
                isEmphasised
                label='SKU Name'
                isEditing={isEditing}
                value={draft.name || notSet}
                editor={
                  <PanelInput
                    ariaLabel='SKU name'
                    value={draft.name}
                    onChange={(name) => patch({ name })}
                  />
                }
              />
            </div>

            <div className='flex min-w-[230px] flex-1 flex-col rounded-lg border border-gray-200 bg-white px-3.5 py-2.5'>
              <p className='mb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-400'>
                SKU Barcode
                <span className='ml-1 font-medium text-gray-300'>
                  (optional)
                </span>
              </p>
              {isEditing ? (
                <PanelInput
                  ariaLabel='SKU barcode'
                  maxLength={13}
                  placeholder='12 - 13 digits'
                  value={draft.barcode}
                  onChange={(barcode) => patch({ barcode })}
                />
              ) : (
                <p className='text-base font-semibold tracking-wider text-gray-900'>
                  {draft.barcode || notSet}
                </p>
              )}
              <Ean13Barcode barcode={draft.barcode} className='mt-auto block' />
              {!draft.barcode && (
                <p className='mt-2 text-xs text-gray-400'>No barcode yet</p>
              )}
            </div>

            <div className='flex min-w-[170px] flex-1 flex-col gap-2.5'>
              <PanelFieldBox
                isEmphasised
                label='SKU Type'
                isEditing={isEditing}
                value={draft.unitOfMeasure || notSet}
                editor={
                  <PanelSelect
                    ariaLabel='SKU type'
                    value={draft.unitOfMeasure}
                    options={SKU_TYPE_OPTIONS}
                    onChange={(unitOfMeasure) => patch({ unitOfMeasure })}
                  />
                }
              />
              <PanelFieldBox
                isEmphasised
                label='Product per SKU'
                isEditing={isEditing}
                value={draft.amountOfProductInSku || notSet}
                editor={
                  <PanelInput
                    ariaLabel='Product per SKU'
                    value={draft.amountOfProductInSku}
                    onChange={(amountOfProductInSku) =>
                      patch({ amountOfProductInSku })
                    }
                  />
                }
              />
            </div>
          </div>

          <div className='mt-2.5 flex flex-wrap items-center gap-3'>
            <PanelFieldBox
              isEmphasised
              className='min-w-[330px] flex-1'
              label='Dimensions (L × W × H)'
              isEditing={isEditing}
              value={dimensionsText ?? notSet}
              editor={
                <DimensionsEditor
                  label='SKU dimensions'
                  unitOptions={DIMENSION_UNIT_OPTIONS}
                  value={draft.dimensions}
                  onChange={(dimensions) => patch({ dimensions })}
                />
              }
            />
            <PanelFieldBox
              isEmphasised
              className='min-w-[330px] flex-1'
              label='Gross weight & volume'
              isEditing={isEditing}
              value={
                draft.grossWeight || draft.grossVolume ? (
                  <span className='flex items-center gap-2'>
                    <span>
                      {draft.grossWeight
                        ? `${draft.grossWeight} ${draft.grossWeightUnitOfMeasure}`
                        : '—'}
                    </span>
                    <span className='text-gray-300'>|</span>
                    <span>
                      {draft.grossVolume
                        ? `${draft.grossVolume} ${unitLabel(
                            draft.grossVolumeUnitOfMeasure
                          )}`
                        : '—'}
                    </span>
                  </span>
                ) : (
                  notSet
                )
              }
              editor={
                <PanelControlRow>
                  <PanelInput
                    isCompact
                    ariaLabel='SKU gross weight'
                    value={draft.grossWeight}
                    onChange={(grossWeight) => patch({ grossWeight })}
                  />
                  <PanelSelect
                    isAuto
                    ariaLabel='SKU gross weight unit'
                    value={draft.grossWeightUnitOfMeasure}
                    options={WEIGHT_UNIT_OPTIONS}
                    onChange={(grossWeightUnitOfMeasure) =>
                      patch({ grossWeightUnitOfMeasure })
                    }
                  />
                  <span className='text-gray-300'>|</span>
                  <PanelInput
                    isCompact
                    ariaLabel='SKU gross volume'
                    value={draft.grossVolume}
                    onChange={(grossVolume) => patch({ grossVolume })}
                  />
                  <PanelSelect
                    isAuto
                    ariaLabel='SKU gross volume unit'
                    value={draft.grossVolumeUnitOfMeasure}
                    options={VOLUME_UNIT_OPTIONS}
                    formatOption={unitLabel}
                    onChange={(grossVolumeUnitOfMeasure) =>
                      patch({ grossVolumeUnitOfMeasure })
                    }
                  />
                </PanelControlRow>
              }
            />
            {canManageSkus && (
              <button
                type='button'
                className='ml-auto inline-flex h-10 items-center gap-1.5 rounded-lg border border-primary bg-white px-4 text-sm font-medium text-primary hover:bg-blue-50'
                onClick={onAddPallet}
              >
                <PlusIcon className='h-4 w-4' />
                Add pallet type
              </button>
            )}
          </div>

          <PanelFieldBox
            isOptional
            className='mt-2.5'
            label='Description'
            isEditing={isEditing}
            value={draft.description || notSet}
            editor={
              <PanelTextarea
                rows={2}
                ariaLabel='SKU description'
                value={draft.description}
                onChange={(description) => patch({ description })}
              />
            }
          />

          <div className='mt-3.5'>
            <PackagingHierarchy
              sku={skuAsEdited}
              title='Packaging hierarchy'
              renderStepBody={(step) =>
                step.palletIndex === null ? null : (
                  <div className='border-t border-gray-200 p-2'>
                    {renderPalletStep(step.palletIndex)}
                  </div>
                )
              }
            />
            {draftPallet && <div className='mt-2'>{draftPallet}</div>}
          </div>

          <div className='mt-3.5'>
            <button
              type='button'
              aria-expanded={isFurtherInformationOpen}
              className='inline-flex items-center gap-2 text-sm font-semibold text-gray-700'
              onClick={() => setIsFurtherInformationOpen((isOpen) => !isOpen)}
            >
              Further information
              <ChevronDownIcon
                className={`h-4 w-4 text-gray-500 transition-transform ${
                  isFurtherInformationOpen ? 'rotate-180' : ''
                }`}
              />
            </button>
            {isFurtherInformationOpen && (
              <>
                <div className='mt-2.5 grid grid-cols-3 gap-3'>
                  <PanelFieldBox
                    isOptional
                    label='MOQ (Minimum order q-ty)'
                    isEditing={isEditing}
                    value={draft.minimumOrderQuantity || notSet}
                    editor={
                      <PanelInput
                        ariaLabel='Minimum order quantity'
                        value={draft.minimumOrderQuantity}
                        onChange={(minimumOrderQuantity) =>
                          patch({ minimumOrderQuantity })
                        }
                      />
                    }
                  />
                  <PanelFieldBox
                    isOptional
                    className='col-span-2'
                    label='Co-packing supplier'
                    isEditing={isEditing}
                    value={draft.coPackingSupplier || notSet}
                    editor={
                      <PanelInput
                        ariaLabel='Co-packing supplier'
                        value={draft.coPackingSupplier}
                        onChange={(coPackingSupplier) =>
                          patch({ coPackingSupplier })
                        }
                      />
                    }
                  />
                </div>

                <AdditionalFieldRows
                  fields={draft.additionalFields}
                  isEditing={isEditing}
                  isAddFieldOffered={isEditing || canManageSkus}
                  onFieldChange={updateAdditionalField}
                  onRemoveField={(index) =>
                    patch({
                      additionalFields: draft.additionalFields.filter(
                        (_, fieldIndex) => fieldIndex !== index
                      ),
                    })
                  }
                  onAddField={
                    isEditing
                      ? () =>
                          patch({
                            additionalFields: [
                              ...draft.additionalFields,
                              { name: '', value: '' },
                            ],
                          })
                      : onAddAdditionalField
                  }
                />
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
